import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export function FlickeringGrid({ className, cellCount = 120, squareSize = 12, gap = 6 }) {
  const containerRef = useRef(null);
  const [resolvedCellCount, setResolvedCellCount] = useState(cellCount);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const computeCellCount = () => {
      const rect = element.getBoundingClientRect();
      const width = Math.max(0, rect.width);
      const height = Math.max(0, rect.height);
      const stride = Math.max(1, Number(squareSize) + Number(gap));
      const columns = Math.max(1, Math.ceil(width / stride));
      const rows = Math.max(1, Math.ceil(height / stride));
      const target = columns * rows;
      setResolvedCellCount(Math.max(Number(cellCount) || 120, target));
    };

    computeCellCount();

    const observer = new ResizeObserver(() => {
      computeCellCount();
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [cellCount, squareSize, gap]);

  const cells = useMemo(
    () =>
      Array.from({ length: resolvedCellCount }, (_, index) => ({
        id: index,
        peak: (0.2 + Math.random() * 0.55).toFixed(2),
        duration: (1.3 + Math.random() * 1.8).toFixed(2),
        delay: (Math.random() * 1.8).toFixed(2)
      })),
    [resolvedCellCount]
  );

  return (
    <div
      ref={containerRef}
      className={cn("grid h-full w-full", className)}
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${squareSize}px, 1fr))`,
        gridAutoRows: `${squareSize}px`,
        gap: `${gap}px`
      }}
      aria-hidden="true"
    >
      {cells.map((cell) => (
        <span
          key={cell.id}
          className="flicker-grid-cell rounded-[3px] bg-cyan-300"
          style={{
            animationDuration: `${cell.duration}s`,
            animationDelay: `${cell.delay}s`,
            "--flicker-peak": cell.peak
          }}
        />
      ))}
    </div>
  );
}
